/**
 * Live download progress over Server-Sent Events.
 *
 * SSE rather than WebSockets: the traffic is one-directional, it reconnects on
 * its own, and it rides the same session cookie as every other request. The
 * `useJobs` query polls as a fallback, so a dropped stream degrades to a slower
 * progress bar rather than a stuck one.
 */

import { useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef, useState } from 'react'

import { keys } from './hooks'
import type { DownloadJob, JobEvent, Paginated } from './types'

export type JobEventHandler = (event: JobEvent) => void

export function useJobStream(enabled: boolean, onEvent?: JobEventHandler) {
  const queryClient = useQueryClient()
  const [connected, setConnected] = useState(false)
  const handlerRef = useRef(onEvent)
  handlerRef.current = onEvent

  useEffect(() => {
    if (!enabled) return

    let source: EventSource | null = null
    let retryTimer: number | undefined
    let attempt = 0
    let cancelled = false

    const connect = () => {
      if (cancelled) return
      source = new EventSource('/api/v1/jobs/events', { withCredentials: true })

      source.onopen = () => {
        attempt = 0
        setConnected(true)
      }

      source.addEventListener('job', (message) => {
        try {
          const event = JSON.parse((message as MessageEvent).data) as JobEvent
          applyToCache(queryClient, event)
          handlerRef.current?.(event)
        } catch {
          /* a malformed frame is not worth tearing the stream down for */
        }
      })

      source.onerror = () => {
        setConnected(false)
        source?.close()
        if (cancelled) return
        // Back off up to 30s so a restarting backend is not hammered.
        attempt += 1
        const delay = Math.min(1000 * 2 ** (attempt - 1), 30_000)
        retryTimer = window.setTimeout(connect, delay)
      }
    }

    connect()

    return () => {
      cancelled = true
      window.clearTimeout(retryTimer)
      source?.close()
      setConnected(false)
    }
  }, [enabled, queryClient])

  return { connected }
}

/**
 * Patch the cached job list in place.
 *
 * Refetching on every tick would mean a request per progress update per user;
 * merging the event is what keeps a smooth progress bar cheap. A job the cache
 * has not seen yet triggers one invalidation to pull in its nested track.
 */
function applyToCache(
  queryClient: ReturnType<typeof useQueryClient>,
  event: JobEvent,
): void {
  let matched = false

  for (const status of ['active', 'all'] as const) {
    queryClient.setQueryData<Paginated<DownloadJob>>(keys.jobs(status), (previous) => {
      if (!previous) return previous
      const index = previous.results.findIndex((job) => job.id === event.id)
      if (index === -1) return previous
      matched = true

      const merged: DownloadJob = { ...previous.results[index], ...stripType(event) }
      const results = [...previous.results]
      results[index] = merged
      return { ...previous, results }
    })
  }

  if (!matched) {
    void queryClient.invalidateQueries({ queryKey: ['jobs'] })
  }

  // A finished job changes the library: the track is now playable, or failed.
  if (event.status === 'succeeded' || event.status === 'failed') {
    void queryClient.invalidateQueries({ queryKey: ['library'] })
    void queryClient.invalidateQueries({ queryKey: ['search'] })
    void queryClient.invalidateQueries({ queryKey: ['artists'] })
    if (event.status === 'succeeded') {
      void queryClient.invalidateQueries({ queryKey: keys.storage })
    }
  }
}

function stripType(event: JobEvent): Omit<JobEvent, 'type'> {
  const { type: _ignored, ...rest } = event
  return rest
}
